import { useEffect, useRef, useState } from "react";
import { api, uploadFile, fileUrl } from "@/lib/api";
import { PageHeader, StatusBadge, FileLink } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Plus, PackageCheck, Trash2, ScanLine, X } from "lucide-react";
import { toast } from "sonner";

function fmtDate(d) { if (!d) return "—"; try { return new Date(d).toLocaleDateString("de-DE"); } catch { return d; } }

function ScanUpload({ fileId, onResult }) {
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const isImage = (f.type || "").startsWith("image/");
      if (isImage) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/goods/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onResult(data); // {file_id, supplier, date, mhd, temperature}
        const got = [data.supplier, data.date, data.mhd, data.temperature].some((v) => v != null);
        toast.success(got ? "Lieferschein erkannt – bitte Werte prüfen" : "Lieferschein gespeichert");
      } else {
        const res = await uploadFile(f);
        onResult({ file_id: res.file_id });
        toast.success("Lieferschein (PDF) gespeichert");
      }
    } catch { toast.error("Upload/Erkennung fehlgeschlagen"); } finally { setBusy(false); }
  };

  if (fileId) {
    return (
      <div className="flex items-center gap-2">
        <a href={fileUrl(fileId)} target="_blank" rel="noreferrer">
          <img src={fileUrl(fileId)} alt="Lieferschein" className="h-16 w-16 rounded-lg object-cover border border-slate-200" onError={(e) => { e.target.style.display = "none"; }} />
        </a>
        <span className="text-sm text-emerald-600">Lieferschein hinterlegt</span>
        <button type="button" onClick={() => onResult({ file_id: null })} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handle} data-testid="input-goods-scan-camera" />
      <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handle} data-testid="input-goods-scan-file" />
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => camRef.current?.click()}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ScanLine className="h-4 w-4 mr-1" />} Scannen (Kamera)
      </Button>
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => fileRef.current?.click()}>
        Datei/PDF wählen
      </Button>
      {busy && <span className="text-xs text-slate-500">KI liest Lieferschein …</span>}
    </div>
  );
}

export default function WareneingangPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ supplier: "", date: new Date().toISOString().slice(0, 10), temperature: "", mhd: "", status: "ok", note: "", file_id: null });

  const load = () => api.get("/goods").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.supplier.trim()) { toast.error("Lieferant erforderlich"); return; }
    setBusy(true);
    try {
      await api.post("/goods", { ...form, temperature: form.temperature === "" ? null : parseFloat(form.temperature) });
      toast.success("Wareneingang erfasst");
      setForm({ supplier: "", date: new Date().toISOString().slice(0, 10), temperature: "", mhd: "", status: "ok", note: "", file_id: null });
      setOpen(false); load();
    } catch { toast.error("Fehler"); } finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/goods/${id}`); load(); };

  return (
    <div>
      <PageHeader title="Wareneingangskontrolle" subtitle="Lieferant, Temperatur, MHD, Zustand – mit Lieferschein-Scan">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-add-goods"><Plus className="h-4 w-4 mr-1" /> Neue Lieferung</Button></DialogTrigger>
          <DialogContent data-testid="goods-dialog">
            <DialogHeader><DialogTitle>Wareneingang erfassen</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Lieferant *</Label>
                  <Input data-testid="input-goods-supplier" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Datum</Label>
                  <Input type="date" data-testid="input-goods-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Temperatur (°C)</Label>
                  <Input type="number" step="0.1" data-testid="input-goods-temp" value={form.temperature} onChange={(e) => setForm({ ...form, temperature: e.target.value })} placeholder="bei Kühl/TK" /></div>
                <div className="space-y-1.5"><Label>MHD</Label>
                  <Input type="date" data-testid="input-goods-mhd" value={form.mhd} onChange={(e) => setForm({ ...form, mhd: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="select-goods-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ok">In Ordnung</SelectItem>
                    <SelectItem value="rejected">Beanstandet</SelectItem>
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Lieferschein (Foto/Scan oder PDF) – KI liest Werte aus</Label>
                <ScanUpload fileId={form.file_id} onResult={(r) => setForm((prev) => ({
                  ...prev,
                  file_id: r.file_id ?? null,
                  supplier: r.supplier ?? prev.supplier,
                  date: r.date ?? prev.date,
                  mhd: r.mhd ?? prev.mhd,
                  temperature: r.temperature != null ? String(r.temperature) : prev.temperature,
                }))} /></div>
              <div className="space-y-1.5"><Label>Bemerkung</Label>
                <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="optional" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button data-testid="btn-submit-goods" onClick={submit} disabled={busy}>{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Laden …</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-400"><PackageCheck className="h-10 w-10 mx-auto mb-2" />Noch keine Lieferungen erfasst</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="goods-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Datum</th><th className="px-4 py-3">Lieferant</th><th className="px-4 py-3">Temp.</th><th className="px-4 py-3">MHD</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Lieferschein</th><th className="px-4 py-3">Bemerkung</th><th></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((i) => (
                <tr key={i.id} className="hover:bg-slate-50" data-testid="goods-row">
                  <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(i.date)}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{i.supplier}</td>
                  <td className="px-4 py-2.5 font-mono">{i.temperature != null ? `${i.temperature}°C` : "—"}</td>
                  <td className="px-4 py-2.5">{fmtDate(i.mhd)}</td>
                  <td className="px-4 py-2.5">{i.status === "ok" ? <StatusBadge variant="done">In Ordnung</StatusBadge> : <StatusBadge variant="warning">Beanstandet</StatusBadge>}</td>
                  <td className="px-4 py-2.5"><FileLink fileId={i.file_id} label="Lieferschein" /></td>
                  <td className="px-4 py-2.5 text-slate-500">{i.note || "—"}</td>
                  <td className="px-4 py-2.5"><button onClick={() => del(i.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
